import { Lightbulb, FileEdit, Eye, CalendarCheck, CheckCircle2 } from 'lucide-react';
import type { ContentItem, ContentStatus } from '../../types/content';
import { useContentStore } from '../../stores/contentStore';
import EmptyState from '../common/EmptyState';
import ContentCard from './ContentCard';

const COLUMNS: Array<{ key: ContentStatus; label: string; icon: typeof Lightbulb }> = [
  { key: 'idea', label: 'Idea', icon: Lightbulb },
  { key: 'draft', label: 'Draft', icon: FileEdit },
  { key: 'review', label: 'Review', icon: Eye },
  { key: 'scheduled', label: 'Scheduled', icon: CalendarCheck },
  { key: 'published', label: 'Published', icon: CheckCircle2 },
];

interface ContentKanbanProps {
  onItemClick: (item: ContentItem) => void;
}

export default function ContentKanban({ onItemClick }: ContentKanbanProps) {
  const items = useContentStore((s) => s.items);
  const updateContent = useContentStore((s) => s.updateContent);

  function handleStatusChange(id: number, status: ContentStatus) {
    updateContent(id, { status });
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
      {COLUMNS.map((col) => {
        const columnItems = items.filter((item) => item.status === col.key);
        const Icon = col.icon;

        return (
          <div key={col.key} className="flex flex-col gap-3">
            <div className="flex items-center gap-2 px-1">
              <Icon className="h-4 w-4 text-text-muted" />
              <h3 className="text-sm font-medium text-text-primary">{col.label}</h3>
              <span className="bg-bg-tertiary rounded-full px-2 text-xs text-text-muted">
                {columnItems.length}
              </span>
            </div>

            <div className="flex flex-col gap-2 min-h-[120px]">
              {columnItems.length > 0 ? (
                columnItems.map((item) => (
                  <ContentCard
                    key={item.id}
                    item={item}
                    onStatusChange={handleStatusChange}
                    onClick={() => onItemClick(item)}
                  />
                ))
              ) : (
                <EmptyState title="No items" description={`No ${col.label.toLowerCase()} content`} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
